// @ts-nocheck
/**
 * Derived from OrderConfirmationProductList in store blueprint
 */
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import Loader from "@store/Library/Components/Toolkit/Loader";
import { orderProps } from "@store/Themes/common/props/order";
import useSplitOrderObjectMapper from "@store/Framework/Common/Hooks/ReviewAndSubmit/useSplitOrderObjectMapper";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import CartLinesList, { CartLinesListStyles } from "@store/Overrides/Components/CartLinesList";
import { infoProps } from "@store/Themes/common/props/info";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { gridGapStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showLineNumber = "showLineNumber",
}

const mapStateToProps = (state: ApplicationState) => ({
    cartState: getCartState(state, state.pages.orderConfirmation.cartId),
    promotionsDataView: getPromotionsDataView(state, state.pages.orderConfirmation.cartId),
    settingsCollection: getSettingsCollection(state),
    language: state.context.session.language,
});

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> & {
        [fields.showLineNumber]: boolean;
    };

export interface OrderConfirmationSplitOrderProductListStyles {
    centeringWrapper?: InjectableCss;
    orderLinesList?: CartLinesListStyles;
    block?: BlockProps;
    wrapper?: InjectableCss;
    infoTitle?: TypographyProps;
    infoValue?: TypographyProps;
    orderContainer?: GridContainerProps;
    orderTitleItem?: GridItemProps;
    orderTitleWrapper?: InjectableCss;
    orderInfosContainer?: GridContainerProps;
    orderInfosItem?: GridItemProps;
    orderInfoItem?: GridItemProps;
    orderErrors?: GridItemProps;
    orderWarnings?: GridItemProps;
    orderNotesItem?: GridItemProps;
    orderLinesItem?: GridItemProps;
    orderSummaryItem?: GridItemProps;
    orderSummaryContainer?: GridContainerProps;
    summarySpacerItem?: GridItemProps;
    summaryInfosItem?: GridItemProps;
}

export const productListStyles: OrderConfirmationSplitOrderProductListStyles = {
    block: orderProps.block,
    infoTitle: infoProps.label,
    infoValue: mergeProps<TypographyProps>(infoProps.value, {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    }),
    orderContainer: {
        css: css`
            ${spacingStyles(pxToRem(24), pxToRem(48))}
        `,
    },
    orderTitleWrapper: {
        css: css`
            width: 100%;
            padding: ${pxToRem(16)} 0;
            border-bottom: 3px solid ${getColor("common.border")};
        `,
    },
    orderTitleItem: {
        width: 12,
    },
    orderInfosItem: {
        width: 12,
    },
    orderInfosContainer: {
        css: css`
            padding: ${pxToRem(8)} 0 ${pxToRem(32)} 0;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    orderInfoItem: {
        width: [6, 6, 4, 3, 3],
    },
    orderErrors: {
        width: 12,
    },
    orderWarnings: {
        width: 12,
    },
    orderNotesItem: {
        width: [12, 12, 12, 10, 8],
    },
    orderLinesItem: {
        width: 12,
    },
    orderLinesList: {
        wrapper: {
            css: css`
                width: 100%;
                flex-basis: 100%;
            `,
        },
        cardExpanded: {
            productPriceAndQuantityContainer: {
                offsetCss: css`
                    justify-content: flex-end;
                `,
            },
            requestedDateGridItem: {
                css: css`
                    margin-top: ${pxToRem(16)};
                `,
            },
        },
    },
    orderSummaryItem: {
        width: 12,
    },
    orderSummaryContainer: {
        css: css`
            padding-top: ${pxToRem(16)};
            padding-bottom: ${pxToRem(24)};
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    summarySpacerItem: {
        width: [0, 0, 0, 0, 4],
    },
    summaryInfosItem: {
        width: [6, 3, 6, 3, 2],
    },
};

const styles = productListStyles;

const StyledUL = getStyledWrapper("ul");
const StyledLI = getStyledWrapper("li");

const OrderConfirmationSplitOrderProductList: FC<Props> = ({
    cartState,
    promotionsDataView,
    settingsCollection,
    language,
}) => {
    const [isCondensed, setIsCondensed] = useState(false);

    if (!cartState.value || !promotionsDataView.value) {
        return (
            <Block {...styles.block}>
                <Loader />
            </Block>
        );
    }

    const { splitOrders } = cartState.value.properties;
    const fakeCarts = useSplitOrderObjectMapper(splitOrders, cartState.value);

    const { productSettings } = settingsCollection;

    const parsedOrders = (splitOrderJson: string) => {
        return JSON.parse(splitOrderJson);
    };

    return (
        <Block {...styles.block}>
            {fakeCarts.map((cart, index) => (
                <GridContainer key={cart.key} {...styles.orderContainer}>
                    <GridItem {...styles.orderTitleItem}>
                        <StyledWrapper {...styles.orderTitleWrapper}>
                            <Typography forwardAs="h4" variant="h3">
                                {`${index + 1}. `}
                                {cart.erpOrderNumber
                                    ? `${translate("Greif Order Number")}${translate(":")} ${cart.erpOrderNumber}`
                                    : `${translate("Order Number")}${translate(":")} ${cart.key}`}
                            </Typography>
                        </StyledWrapper>
                    </GridItem>
                    {cart.errorMessages.length > 0 && (
                        <GridItem {...styles.orderErrors}>
                            <InfosBox type="error">
                                <StyledUL>
                                    {cart.errorMessages.map((error, index) => (
                                        <StyledLI key={index}>{error}</StyledLI>
                                    ))}
                                </StyledUL>
                            </InfosBox>
                        </GridItem>
                    )}
                    {cart.warningMessages.length > 0 && (
                        <GridItem {...styles.orderWarnings}>
                            <InfosBox type="warning">
                                <StyledUL>
                                    {cart.warningMessages.map((warning, index) => (
                                        <StyledLI key={index}>{warning}</StyledLI>
                                    ))}
                                </StyledUL>
                            </InfosBox>
                        </GridItem>
                    )}
                    <GridItem {...styles.orderInfosItem}>
                        <GridContainer {...styles.orderInfosContainer}>
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoTitle} id="orderDate">
                                        {translate("Order Date")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby="orderDate"
                                        data-test-selector="orderDate"
                                    >
                                        {cart.orderDate?.toLocaleDateString(language?.languageCode)}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoTitle} id="poNumber">
                                        {translate("PO Number")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby="poNumber"
                                        data-test-selector="poNumber"
                                    >
                                        {parsedOrders(splitOrders)[index].customerPo}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoTitle} id="deliveryDate">
                                        {translate("Delivery Date")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby="deliveryDate"
                                        data-test-selector="deliveryDate"
                                    >
                                        {new Date(
                                            parsedOrders(
                                                parsedOrders(splitOrders)[index].lines[0].properties.deliveryDate,
                                            ).requestedDate,
                                        ).toLocaleDateString(language?.languageCode)}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoTitle} id="orderStatus">
                                        {translate("Order Status")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby="orderStatus"
                                        data-test-selector="orderStatus"
                                    >
                                        {cart.statusDisplay}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            {cart.salespersonName && (
                                <GridItem {...styles.orderInfoItem}>
                                    <StyledWrapper {...styles.wrapper}>
                                        <Typography {...styles.infoTitle} id="salespersonName">
                                            {translate("Salesperson")}
                                        </Typography>
                                        <Typography
                                            {...styles.infoValue}
                                            aria-labelledby="salespersonName"
                                            data-test-selector="salespersonName"
                                        >
                                            {cart.salespersonName}
                                        </Typography>
                                    </StyledWrapper>
                                </GridItem>
                            )}
                            {cart.plant && (
                                <GridItem {...styles.orderInfoItem}>
                                    <StyledWrapper {...styles.wrapper}>
                                        <Typography {...styles.infoTitle} id="plant">
                                            {translate("Plant")}
                                        </Typography>
                                        <Typography
                                            {...styles.infoValue}
                                            aria-labelledby="plant"
                                            data-test-selector="plant"
                                        >
                                            {cart.plant}
                                        </Typography>
                                    </StyledWrapper>
                                </GridItem>
                            )}
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoTitle} id="orderCarrier">
                                        {translate("Carrier")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby="orderCarrier"
                                        data-test-selector="orderCarrier"
                                    >
                                        {cart.orderCarrier}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoTitle} id="deliveryTerms">
                                        {translate("Delivery Terms")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby="deliveryTerms"
                                        data-test-selector="deliveryTerms"
                                    >
                                        {cart.deliveryTerms}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.orderInfoItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoTitle} id="paymentTerms">
                                        {translate("Payment Terms")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoValue}
                                        aria-labelledby="paymentTerms"
                                        data-test-selector="paymentTerms"
                                    >
                                        {cart.paymentTerms}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            {cart.notes && (
                                <GridItem {...styles.orderNotesItem}>
                                    <StyledWrapper {...styles.wrapper}>
                                        <Typography {...styles.infoTitle} id="orderNotes">
                                            {translate("Order Notes")}
                                        </Typography>
                                        <Typography
                                            {...styles.infoValue}
                                            aria-labelledby="orderNotes"
                                            data-test-selector="orderNotes"
                                        >
                                            {cart.notes}
                                        </Typography>
                                    </StyledWrapper>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.orderLinesItem}>
                        <CartLinesList
                            key={`${cart.id}_${index}`}
                            cart={cart}
                            promotions={promotionsDataView.value}
                            isCondensed={isCondensed}
                            onChangeIsCondensed={(event: React.SyntheticEvent<Element, Event>, value: boolean) =>
                                setIsCondensed(value)
                            }
                            editable={false}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.orderLinesList}
                            isLineNotesOpen={true}
                            showLineNumber={true}
                            hasSplitOrders={true}
                        />
                    </GridItem>
                    <GridItem {...styles.orderSummaryItem}>
                        <GridContainer {...styles.orderSummaryContainer}>
                            <GridItem {...styles.summarySpacerItem}></GridItem>
                            <GridItem {...styles.summaryInfosItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoValue} id="orderSubtotal">
                                        {translate("Subtotal")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoTitle}
                                        aria-labelledby="orderSubtotal"
                                        data-test-selector="orderSubtotal"
                                    >
                                        {cart.orderSubTotalDisplay}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.summaryInfosItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoValue} id="shippingHandling">
                                        {translate("Shipping & Handling")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoTitle}
                                        aria-labelledby="shippingHandling"
                                        data-test-selector="shippingHandling"
                                    >
                                        {translate("TBD") /* Greif - temporary, like OrderSummary */}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.summaryInfosItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoValue} id="estimatedTax">
                                        {translate("Estimated Tax")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoTitle}
                                        aria-labelledby="estimatedTax"
                                        data-test-selector="estimatedTax"
                                    >
                                        {cart.totalTaxDisplay}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.summaryInfosItem}>
                                <StyledWrapper {...styles.wrapper}>
                                    <Typography {...styles.infoValue} id="orderGrandTotal">
                                        {translate("Total")}
                                    </Typography>
                                    <Typography
                                        {...styles.infoTitle}
                                        aria-labelledby="orderGrandTotal"
                                        data-test-selector="orderGrandTotal"
                                    >
                                        {cart.orderGrandTotalDisplay}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            ))}
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderConfirmationSplitOrderProductList),
    definition: {
        displayName: "Split Order Product List",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
        fieldDefinitions: [
            {
                name: fields.showLineNumber,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                variant: "toggle",
            },
        ],
    },
};

export default widgetModule;
